import {Badge} from "@/components/ui/badge";
import {cn} from "@/lib/utils";

import type {StoredVehiclesKpi} from "../types";
import {DeltaPill} from "./delta-pill";
import {KpiCard, type KpiCardDensity} from "./kpi-card";

type KpiCardStoredVehiclesProps = {
  kpi: StoredVehiclesKpi;
  icon?: React.ElementType;
  density?: KpiCardDensity;
  selected?: boolean;
  onSelect?: () => void;
};

/**
 * Stored vehicles: tracking sessions started in the selected period (primary)
 * plus a snapshot of how many vehicles are in open tracking right now.
 */
export function KpiCardStoredVehicles({
  kpi,
  icon,
  density = "default",
  selected,
  onSelect,
}: KpiCardStoredVehiclesProps) {
  const isCompact = density === "compact";
  const openNow = kpi.secondary?.open_tracking ?? 0;

  if (kpi.stub) {
    return (
      <KpiCard
        label={kpi.label ?? "Stored Vehicles"}
        icon={icon}
        density={density}
        selected={selected}
        onSelect={onSelect}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={cn(
              "text-muted-foreground min-w-0 font-bold tracking-tight tabular-nums",
              isCompact ? "text-3xl" : "text-4xl",
            )}
          >
            —
          </span>
          <Badge
            variant="secondary"
            className={cn("shrink-0 font-medium", isCompact && "text-[10px]")}
          >
            Coming soon
          </Badge>
        </div>
        <p className={cn("text-muted-foreground text-sm leading-snug", isCompact && "mt-auto")}>
          Stored vehicle metrics are not available from this API version yet.
        </p>
      </KpiCard>
    );
  }

  return (
    <KpiCard
      label={kpi.label ?? "Stored Vehicles"}
      icon={icon}
      density={density}
      selected={selected}
      onSelect={onSelect}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <span
          className={cn(
            "text-foreground min-w-0 font-bold tracking-tight tabular-nums",
            isCompact ? "text-3xl" : "text-4xl",
          )}
          title="Tracking sessions started in this period"
        >
          {kpi.current.toLocaleString()}
        </span>
        <DeltaPill value={kpi.delta_pct} size={isCompact ? "sm" : "default"} className="shrink-0" />
      </div>
      <p
        className="text-muted-foreground text-sm leading-snug"
        title="Compared to the previous period of the same length"
      >
        from <span className="tabular-nums">{kpi.previous.toLocaleString()}</span> last period
      </p>
      <div
        className={cn(
          "border-border text-muted-foreground mt-auto flex flex-wrap items-baseline justify-between gap-x-2 gap-y-1 border-t pt-2 text-xs leading-snug",
          !isCompact && "mt-1",
        )}
      >
        <span title="Vehicles currently in open tracking (not limited to the selected period)">
          Open now
        </span>
        <span className="text-foreground font-semibold tabular-nums">{openNow.toLocaleString()}</span>
      </div>
    </KpiCard>
  );
}
